/**
 * Zustand store factory for table filtering and sorting
 * Based on column sort/filter system documentation
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Filter operators
export const TableFilterOperators = {
  EQ: 'eq',
  REGEX: 'regex',
  BETWEEN: 'between',
  IN: 'in',
  IS_EMPTY: 'is_empty',
};

// Field types
export const FieldTypes = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  BOOLEAN: 'boolean',
  SINGLE_CHOICE: 'single_choice',
  MULTI_CHOICE: 'multi_choice',
};

/**
 * Create a Zustand store for table filters
 * @param {Object} config - Store configuration
 * @param {string} config.tableId - Unique table ID for localStorage
 * @param {string} config.initialSort - Initial sort field
 * @param {string} config.initialSearch - Initial search value
 * @param {Array} config.initialColumns - Initial column configuration
 * @param {string} config.initialLimit - Initial page size
 * @param {number} config.initialPage - Initial page number
 * @param {Array} config.limitOptions - Available page size options
 */
export function createTableFiltersStore(config = {}) {
  const {
    tableId = 'default',
    initialSort = '',
    initialSearch = '',
    initialColumns = [],
    initialLimit = '20',
    initialPage = 1,
    limitOptions = ['20', '50', '100'],
  } = config;

  return create(
    persist(
      (set, get) => ({
        // State
        sort: initialSort,
        search: initialSearch,
        rules: [],
        columns: initialColumns,
        page: initialPage,
        limit: initialLimit,
        limitOptions,

        // Actions
        setSort: (sort) => set({ sort, page: 1 }),

        setSearch: (search) => set({ search, page: 1 }),

        upsertRule: (rule) => {
          const { rules } = get();
          const existingIndex = rules.findIndex((r) => r.slug === rule.slug);
          if (existingIndex >= 0) {
            const newRules = [...rules];
            newRules[existingIndex] = rule;
            set({ rules: newRules, page: 1 });
          } else {
            set({ rules: [...rules, rule], page: 1 });
          }
        },

        removeRule: (slug) => {
          const { rules } = get();
          set({ rules: rules.filter((r) => r.slug !== slug), page: 1 });
        },

        setPage: (page) => set({ page }),

        setLimit: (limit) => set({ limit, page: 1 }),

        setColumns: (columns) => set({ columns }),

        resetFilters: () => set({ rules: [], search: '', page: 1 }),

        // Computed - serialize filters for API
        getFiltersForQuery: () => {
          const { rules } = get();
          return rules
            .filter((r) => r.slug && r.value != null && r.value !== '')
            .map((r) => `${r.slug}:${r.operator}:${r.value}`);
        },

        // Get active rule for a column
        getRuleForColumn: (slug) => {
          const { rules } = get();
          return rules.find((r) => r.slug === slug);
        },

        // Check if column has active filter
        hasActiveFilter: (slug) => {
          const { rules } = get();
          return rules.some((r) => r.slug === slug && r.value);
        },
      }),
      {
        name: `table-columns-${tableId}`,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          columns: state.columns,
          limit: state.limit,
        }),
        merge: (persistedState, currentState) => ({
          ...currentState,
          columns: persistedState?.columns ?? currentState.columns,
          limit: persistedState?.limit ?? currentState.limit,
        }),
      }
    )
  );
}

export default createTableFiltersStore;